import { useEffect, useRef, useState } from 'react';

/** Aantal tekens dat per seconde onthuld wordt tijdens het typewriter-effect. */
const CHARS_PER_SECOND = 60;

/**
 * Onthult `target` progressief zolang `enabled` waar is (gesimuleerd typewriter-effect
 * op een reeds volledig ontvangen string). Roept `onComplete` één keer aan wanneer de
 * volledige tekst getoond is. Wanneer `enabled` onwaar is, wordt `target` direct volledig
 * teruggegeven.
 */
export function useTypewriter(
  target: string,
  enabled: boolean,
  onComplete?: () => void,
): string {
  const [displayed, setDisplayed] = useState(enabled ? '' : target);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!enabled) {
      setDisplayed(target);
      return;
    }

    setDisplayed('');
    let frame = 0;
    let startTs: number | null = null;

    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const elapsedSeconds = (ts - startTs) / 1000;
      const count = Math.min(target.length, Math.floor(elapsedSeconds * CHARS_PER_SECOND));
      setDisplayed(target.slice(0, count));

      if (count < target.length) {
        frame = requestAnimationFrame(tick);
      } else {
        onCompleteRef.current?.();
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, enabled]);

  return displayed;
}
